import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Play, ArrowRight, Star, X } from 'lucide-react';
import { heroImage } from '../data/mock';
import { useLang } from '../context/LanguageContext';

export default function HeroSection() {
  const [videoOpen, setVideoOpen] = useState(false);
  const { t, lang } = useLang();

  return (
    <section className="relative bg-[#fbf7ee] overflow-hidden min-h-[820px] md:min-h-[760px] lg:min-h-[860px]">
      <div className="absolute inset-0">
        <img src={heroImage} alt="WYSE Tribe advisors" className="w-full h-full object-cover" />
        <div className="absolute inset-0 hero-overlay" />
        {lang === 'zh' && <div data-testid="hero-zh-lighten" className="absolute inset-0 bg-white/45 md:bg-white/30" />}
      </div>
      <div className="absolute top-10 right-10 w-40 h-40 dotted-pattern opacity-60 pointer-events-none hidden md:block" />

      <div className="relative max-w-[1280px] mx-auto px-6 pt-20 pb-44 md:pt-28 md:pb-48 lg:pb-56">
        <div className="max-w-2xl">
          <div className="inline-flex items-center gap-2 bg-white/85 backdrop-blur border border-[#f5b300]/30 rounded-full px-4 py-1.5 mb-6 shadow-sm fade-in-up">
            <span className="flex items-center gap-0.5 text-[#f5b300]">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="w-3.5 h-3.5 fill-current" />
              ))}
            </span>
            <span className="text-[12px] text-neutral-700 font-medium">{t('hero.trusted')}</span>
          </div>

          <p className="section-eyebrow mb-4 fade-in-up" style={{ animationDelay: '0.05s' }}>{t('hero.eyebrow')}</p>
          <h1 className="text-[44px] md:text-[64px] leading-[1.05] font-extrabold text-[#14110f] fade-in-up" style={{ animationDelay: '0.1s' }}>
            {t('hero.titlePre')} <br className="hidden md:block" />
            {t('hero.titleBreak')} <span className="text-[#f5b300] relative inline-block">{t('hero.titleAccent')}
              <svg viewBox="0 0 200 12" className="absolute -bottom-2 left-0 w-full" fill="none">
                <path d="M2 9C40 3 80 3 120 6C150 8 180 9 198 6" stroke="#f5b300" strokeWidth="3" strokeLinecap="round" />
              </svg>
            </span>
          </h1>
          <p className="mt-6 text-[17px] text-neutral-600 leading-relaxed max-w-xl fade-in-up" style={{ animationDelay: '0.18s' }}>
            {t('hero.desc')}
          </p>

          <div className="mt-9 flex items-center gap-5 fade-in-up" style={{ animationDelay: '0.26s' }}>
            <Link
              to="/book"
              className="inline-flex items-center gap-2 bg-[#f5b300] hover:bg-[#e0a200] text-[#14110f] font-semibold px-7 py-4 rounded-md shadow-[0_14px_30px_-10px_rgba(245,179,0,0.7)] hover:-translate-y-0.5 transition-all"
            >
              {t('cta.getStarted')} <ArrowRight className="w-4 h-4" />
            </Link>
            <button
              onClick={() => setVideoOpen(true)}
              className="flex items-center gap-3 group"
            >
              <span className="w-14 h-14 rounded-full bg-[#4a2e1a] text-white flex items-center justify-center shadow-[0_10px_30px_-10px_rgba(74,46,26,0.7)] group-hover:scale-105 transition-transform relative">
                <Play className="w-5 h-5 fill-current ml-0.5" />
                <span className="absolute inset-0 rounded-full border-2 border-[#4a2e1a]/40 animate-ping" />
              </span>
              <span className="text-[14px] font-semibold text-[#14110f]">{t('cta.watchStory')}</span>
            </button>
          </div>
        </div>
      </div>

      {videoOpen && (
        <div className="fixed inset-0 z-[100] bg-black/85 backdrop-blur flex items-center justify-center p-4" onClick={() => setVideoOpen(false)}>
          <button className="absolute top-6 right-6 w-11 h-11 rounded-full bg-white/10 text-white flex items-center justify-center hover:bg-white/20" onClick={() => setVideoOpen(false)}>
            <X className="w-5 h-5" />
          </button>
          <div className="w-full max-w-4xl aspect-video rounded-xl overflow-hidden shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <iframe
              width="100%"
              height="100%"
              src="https://www.youtube.com/embed/VhBl3dHT5SY?autoplay=1"
              title="WYSE Tribe"
              frameBorder="0"
              allow="autoplay; encrypted-media"
              allowFullScreen
            />
          </div>
        </div>
      )}
    </section>
  );
}
